import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { XCircle, ArrowRight, Mail, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { getCancelledCheckoutDetails } from "@/lib/membership.functions";

type Category = "auth_required" | "declined" | "cancelled";

const searchSchema = z.object({
  session_id: z.string().optional(),
});

export const Route = createFileRoute("/checkout/cancelled")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Payment Not Completed — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your payment was not completed. Your Membership spot is still available.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CheckoutCancelledPage,
});

function CheckoutCancelledPage() {
  const { session_id } = Route.useSearch();
  const logDecline = useServerFn(getCancelledCheckoutDetails);
  const loggedRef = useRef(false);
  // Default to the generic reassuring message; only upgrade to auth-required
  // wording if Stripe told us that's what happened.
  const [category, setCategory] = useState<Category>("cancelled");

  useEffect(() => {
    if (!session_id || loggedRef.current) return;
    loggedRef.current = true;
    logDecline({ data: { session_id } })
      .then((r) => {
        if (r && "ok" in r && r.ok) setCategory(r.category);
      })
      .catch(() => {
        // best-effort; keep the generic message
      });
  }, [session_id, logDecline]);

  const isAuth = category === "auth_required";
  const Icon = isAuth ? ShieldCheck : XCircle;

  return (
    <div className="min-h-[100svh] bg-gradient-to-b from-background via-background to-[var(--gold-soft)]/25 px-4 py-16 sm:py-24">
      <div className="mx-auto flex max-w-[720px] items-center justify-center">
        <div className="w-full rounded-[2.25rem] border border-gold/30 bg-gradient-to-b from-card/95 to-card/90 px-6 py-10 text-center shadow-[0_30px_80px_-30px_color-mix(in_oklab,var(--gold-deep)_25%,transparent)] backdrop-blur sm:px-12 sm:py-14">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Icon className="h-8 w-8" strokeWidth={2} />
          </div>
          <h1 className="mt-5 font-display text-3xl leading-tight text-foreground sm:text-[38px]">
            {isAuth
              ? "Your bank needs to verify this payment"
              : "Your payment wasn't completed"}
          </h1>
          {isAuth ? (
            <p className="mx-auto mt-4 max-w-lg text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]">
              Your bank requires an additional identity check. Please try
              again and complete any verification requested by your bank. You
              have not been charged.
            </p>
          ) : (
            <p className="mx-auto mt-4 max-w-lg text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]">
              Your bank or payment provider did not approve this attempt. You
              have not been charged.
              <br />
              <br />
              Please wait a moment and try again, use a different payment
              method, or contact your bank if the problem continues.
            </p>
          )}
          <div className="mt-9 flex flex-col items-stretch gap-3 sm:items-center">
            <Link
              to="/membership"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-8 py-4 font-display text-[16px] font-medium text-foreground shadow-[var(--shadow-gold)] transition-all duration-300 hover:-translate-y-0.5 sm:w-auto sm:px-10 sm:text-[17px]"
            >
              Try payment again
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/membership"
              className="rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Return to Membership
            </Link>
            <a
              href="mailto:support@recoverytrauma.com"
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <Mail className="h-4 w-4" /> Contact support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
